/**
 * URL rules for the landing page builder's Thank You panel, mirroring the API's
 * `ThankYouSettingsValidator` so the dashboard and the server's save path never disagree.
 *
 * The panel saves as you type, and until ORB-760 there was no client-side check at all: every
 * half-typed URL went to the API, came back 422, and toasted. Typing a YouTube link is a sequence
 * of invalid values by definition, so a tester saw a failed request per pause in their typing. The
 * builder now asks here first and simply does not send a value the server is certain to reject —
 * the field says so inline instead.
 *
 * The messages are written for a reader looking at one labelled field, not copied from the server's
 * sentences, which name the field again ("Thank You Pre-Event YouTube URL must be…"). That wording
 * is also wrong on a page that is not for an online event, where the same field is labelled plainly
 * "Video URL" (ORB-826).
 */

import { fieldLimits } from '@/types/field-limits.generated'

/** Mirrors `ThankYouSettingsValidator.YouTubeHosts`. */
const YOUTUBE_HOSTS = new Set([
    'youtube.com', 'www.youtube.com', 'm.youtube.com',
    'youtu.be', 'www.youtu.be',
    'youtube-nocookie.com', 'www.youtube-nocookie.com',
])

/** The parsed URL when `value` is absolute and http(s), else null — the server's first two tests. */
function parseHttpUrl(value: string): URL | null {
    let parsed: URL
    try {
        parsed = new URL(value)
    } catch {
        return null
    }
    return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? parsed : null
}

/** Fields the API validates as a URL. `DownloadUrl` is auto-populated and never typed. */
export type ThankYouUrlField = 'PreEventYouTubeUrl' | 'PostEventYouTubeUrl' | 'CtaUrl'

const URL_FIELDS: ReadonlySet<string> = new Set<ThankYouUrlField>([
    'PreEventYouTubeUrl', 'PostEventYouTubeUrl', 'CtaUrl',
])

export function isThankYouUrlField(field: string): field is ThankYouUrlField {
    return URL_FIELDS.has(field)
}

/**
 * The message to show under `field`, or null when the value is one the API will accept.
 *
 * Blank is always accepted: it clears the field, exactly as the server reads it.
 *
 * Length is checked here rather than left to the inputs' `maxLength`, which is not a rule the save
 * path can lean on: a `maxLength` attribute does not exist until the field-limits request answers,
 * and it never arrives at all if that request fails. The cap comes from the generated limits, which
 * are emitted from the same server constants the validator reads, so the two cannot drift.
 */
export function thankYouUrlError(field: string, value: string): string | null {
    if (!isThankYouUrlField(field)) return null
    if (value.trim() === '') return null

    const maxLength = fieldLimits.ThankYou[field].MaxLength
    if (value.length > maxLength) return `Too long — ${maxLength} characters at most`

    const parsed = parseHttpUrl(value)
    if (!parsed) return 'Enter a full URL, starting with http:// or https://'

    if (field === 'CtaUrl') return null

    return YOUTUBE_HOSTS.has(parsed.hostname.toLowerCase())
        ? null
        : 'Enter a YouTube link — youtube.com or youtu.be'
}

/**
 * The first message across a whole settings object, or null when every field is saveable.
 *
 * Asked of the panel's live state rather than tracked alongside it. A value withheld from the
 * server is still an edit the user has made — it is simply one that cannot be sent yet — and two
 * places have to know that: generation must not carry it, and a server response must not overwrite
 * it. Deriving both answers from the values themselves means neither can fall out of step with
 * what is on screen.
 */
export function firstThankYouUrlError(settings: object | null | undefined): string | null {
    for (const [field, value] of Object.entries(settings ?? {})) {
        if (typeof value !== 'string') continue
        const error = thankYouUrlError(field, value)
        if (error) return error
    }
    return null
}

/**
 * The fields the panel is holding back — edits the user made that were never sent, so nothing
 * downstream knows they exist.
 *
 * Named per field rather than answered yes/no so the caller can keep exactly those and take the
 * server's value for everything else. One unsaveable URL is not a reason to ignore a change to the
 * CTA label beside it, and "ignore" here has no end: the panel would stay stale until the user
 * happened to fix the URL.
 *
 * The question is asked of each field against `adopted`, the last settings the panel took FROM the
 * server, and a field counts only when the user changed it to something the server will not accept.
 * Two things make that specificity necessary, and both were live bugs:
 *
 * - A page created before the API validated these fields can hold an invalid URL of its own
 *   (CreateAndGenerateAsync took them unchecked until ORB-760). Judging the panel by whether it
 *   contains an invalid value anywhere reads that stored URL as a live edit and wedges the panel
 *   shut for the session.
 * - Comparing the panel against the INCOMING value instead does not fix it: a later response that
 *   legitimately changes another field — a regeneration, or another user — differs from the panel
 *   by that field, so the stale URL still blocks it. The panel has to be judged against what it was
 *   given, not against what it is being offered.
 *
 * Unchanged since the server gave it means it is the server's value, whatever its shape, and the
 * server's own updates to it are not the user's to protect.
 */
export function withheldThankYouFields(
    local: object | null | undefined,
    adopted: object | null | undefined,
): string[] {
    const wasGiven = (adopted ?? {}) as Record<string, unknown>
    const withheld: string[] = []

    for (const [field, value] of Object.entries(local ?? {})) {
        if (typeof value !== 'string') continue
        // `?? null` so a field the server omits and one the panel holds as null read alike — a
        // difference in shape is not an edit.
        if (value === (wasGiven[field] ?? null)) continue
        if (thankYouUrlError(field, value)) withheld.push(field)
    }
    return withheld
}
